import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ar from './locales/ar.json';
import { matchRoute } from './seo/routes';

const STORAGE_KEY = 'dc-lang';

/**
 * THE LANGUAGE COMES FROM THE URL.
 *
 * It used to come from ?lang=, then localStorage, then English. That is fine
 * for a returning visitor and useless for everybody else who matters: a
 * crawler has no storage and follows no query string, so one URL served two
 * languages and Google only ever saw one of them. Every Arabic word on this
 * site was unindexable, and the hreflang block made it worse by naming the
 * same URL for en-gb, ar and x-default, which cancels the set out entirely.
 *
 * So /ar and /ar/... ARE Arabic, and everything else is English. Storage is
 * still read, but only to decide where to send somebody who arrives at the
 * root with a remembered preference, and that redirect happens in the router,
 * never here: if this function returned Arabic for an English URL the
 * prerender would write Arabic into build/index.html.
 */
const initialLang = (() => {
  try {
    return matchRoute(window.location.pathname).lang;
  } catch {
    return 'en';
  }
})();

i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, ar: { translation: ar } },
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Keep <html> lang/dir in sync and remember the visitor's choice. The
// prerender captures documentElement.outerHTML, so this is also what puts
// lang="ar" dir="rtl" into the static Arabic files.
const setLangAndDir = (lng) => {
  const isArabic = lng === 'ar';
  document.documentElement.lang = lng;
  document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
  document.body.classList.toggle('arabic-font', isArabic);
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* private mode, non-fatal */
  }
};

setLangAndDir(i18n.language);
i18n.on('languageChanged', setLangAndDir);

/** What the visitor last chose, for the root-only redirect in App.js. */
export function rememberedLang() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'ar' || stored === 'en' ? stored : null;
  } catch {
    return null;
  }
}

export default i18n;
